import * as React from 'react'

import { cn } from '@/lib/utils'

export interface LogoGridItem {
  src: string
  alt: string
}

export interface LogoGridProps {
  /** Optional line above the grid, e.g. "Trusted by the most innovative companies in the world". */
  heading?: string
  logos: LogoGridItem[]
  /**
   * How many logos are shown at once. Defaults to 6 — the grid classes are
   * tuned for exactly 6 (6 cols desktop, 3x2 mobile). If `logos.length`
   * is at or below this count, the grid is static (no rotation).
   */
  visibleCount?: number
  /** Light logos and a left-aligned eyebrow heading, no grid lines, for dark sections. */
  onDark?: boolean
  className?: string
}

const FADE_MS = 1000
// JPEGs carry their own opaque background, which the dark-ground invert would turn into a white block.
const OPAQUE_IMAGE = /\.jpe?g(\?|#|$)/i
const PAUSE_MS = 2500

/**
 * Never shows the same logo twice at once: rotation always swaps a
 * visible slot for a logo currently absent from the grid, one slot at a
 * time (fade out 1s, then fade in the replacement 1s).
 */
export function LogoGrid({ heading, logos, visibleCount = 6, onDark = false, className }: LogoGridProps) {
  const [visible, setVisible] = React.useState(() => logos.slice(0, visibleCount).map((_, i) => i))
  const [hiddenSlot, setHiddenSlot] = React.useState(null as number | null)

  React.useEffect(() => {
    // logos can change identity after mount — reset rotation state to match.
    setVisible(logos.slice(0, visibleCount).map((_, i) => i))
    setHiddenSlot(null)
  }, [logos, visibleCount])

  React.useEffect(() => {
    // Respect reduced-motion (and keep visual-regression captures stable): no
    // random logo rotation — just show the first `visibleCount` logos.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (logos.length <= visibleCount || reduceMotion) return

    let timeoutId: ReturnType<typeof setTimeout>
    let cancelled = false

    const scheduleSwap = () => {
      timeoutId = setTimeout(() => {
        if (cancelled) return
        const slot = Math.floor(Math.random() * visibleCount)
        setHiddenSlot(slot)

        timeoutId = setTimeout(() => {
          if (cancelled) return
          setVisible((current) => {
            const available = logos.map((_, i) => i).filter((i) => !current.includes(i))
            const next = available[Math.floor(Math.random() * available.length)]
            const updated = [...current]
            updated[slot] = next
            return updated
          })
          setHiddenSlot(null)
          scheduleSwap()
        }, FADE_MS)
      }, PAUSE_MS)
    }

    scheduleSwap()
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [logos, visibleCount])

  return (
    <div className={cn('mx-auto max-w-(--maxw) px-5.5 py-16', className)}>
      {heading ? (
        <p
          className={cn(
            'mb-8',
            onDark
              ? 'text-ink-300 font-mono text-xs font-bold tracking-[0.14em] uppercase'
              : 'text-md text-ink-500 text-center'
          )}
        >
          {heading}
        </p>
      ) : null}
      <div className={cn('border', onDark ? 'border-transparent' : 'border-white')}>
        <div className="grid grid-cols-6 max-[1240px]:grid-cols-3">
          {visible
            // logos can briefly lag `visible` after a prop swap — drop indices past its end.
            .filter((logoIndex) => logoIndex < logos.length)
            .map((logoIndex, slot) => {
              const logo = logos[logoIndex]
              return (
                <div
                  key={slot}
                  className={cn(
                    'flex min-h-24 min-w-0 items-center justify-center border-r px-6 py-8 nth-[6n]:border-r-0 max-[1240px]:min-h-20 max-[1240px]:border-b max-[1240px]:px-4 max-[1240px]:py-6 max-[1240px]:nth-[3n]:border-r-0 max-[1240px]:nth-[n+4]:border-b-0',
                    onDark ? 'border-transparent' : 'border-white'
                  )}
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className={cn(
                      'h-auto max-h-8 w-auto max-w-30 object-contain transition-opacity duration-1000 max-[1240px]:max-h-6 max-[1240px]:max-w-22',
                      onDark && !OPAQUE_IMAGE.test(logo.src) && 'brightness-0 invert'
                    )}
                    style={{ opacity: hiddenSlot === slot ? 0 : 1 }}
                  />
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
